import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ADJUSTMENT_KIND_MAP,
  EMPLOYEE_STATUS_MAP,
  LEAVE_STATUS_MAP,
  LEAVE_TYPE_MAP,
  MEMBERSHIP_ROLE_MAP,
  periodLabel,
  type AdjustmentKind,
  type EmployeeStatus,
  type LeaveStatus,
  type LeaveType,
  type MembershipRole,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { date, dateTime, money, num } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, ErrorState, Modal, PageHead } from '../../components/ui';
import { RecordStats, type RecordStat } from '../../components/RecordStats';
import { Attachments } from '../../components/Attachments';
import { EmployeeDialog } from './EmployeesList';
import { EmployeeDocumentsCard } from './Documents';
import { EmployeeBenefitsCard } from './Benefits';

interface Profile {
  id: string;
  code: string | null;
  name: string;
  jobTitle: string | null;
  department: string | null;
  email: string | null;
  phone: string | null;
  nationalId: string | null;
  address: string | null;
  hireDate: string;
  endDate: string | null;
  status: EmployeeStatus;
  baseSalary: string;
  currency: string;
  isSalesRep: boolean;
  annualLeaveBalance: string;
  manager: { id: string; name: string; jobTitle: string | null } | null;
  reports: Array<{
    id: string;
    name: string;
    jobTitle: string | null;
    status: EmployeeStatus;
  }>;
  leaves: Array<{
    id: string;
    type: LeaveType;
    status: LeaveStatus;
    fromDate: string;
    toDate: string;
    days: string;
  }>;
  adjustments: Array<{
    id: string;
    kind: AdjustmentKind;
    amount: string;
    period: string;
    reason: string | null;
  }>;
  payslips: Array<{
    id: string;
    netPay: string;
    run: { period: string; status: string };
  }>;
  account: {
    membershipId: string;
    role: MembershipRole;
    status: string;
    email: string | null;
    lastLoginAt: string | null;
  } | null;
}

/**
 * ما تعرفه شاشة الموظّف عنه.
 *
 * مدير الموارد البشرية يريد الحالة والرصيد، والمحاسب يريد الأساسي، والمدير
 * المباشر يريد «مَن يتبعه» و«آخر دخول» — وثلاثتهم يفتحون هذه الشاشة.
 */
function employeeCards(e: any): RecordStat[] {
  const months = Math.floor(
    (Date.now() - new Date(e.hireDate).getTime()) / (30.44 * 86_400_000),
  );
  const years = Math.floor(months / 12);

  return [
    {
      key: 'status',
      label: 'الحالة',
      group: 'الخدمة',
      icon: 'user',
      color: e.status === 'ACTIVE' ? '#51A521' : '#9BA3AE',
      value: (
        <Badge kind={EMPLOYEE_STATUS_MAP[e.status as EmployeeStatus].kind}>
          {EMPLOYEE_STATUS_MAP[e.status as EmployeeStatus].label}
        </Badge>
      ),
      hint: 'على رأس العمل أم انتهت خدمته.',
    },
    {
      key: 'hireDate',
      label: 'تاريخ التعيين',
      group: 'الخدمة',
      icon: 'calendar',
      color: '#3672EA',
      value: date(e.hireDate),
      numeric: true,
      hint: 'يوم دخوله الشركة.',
    },
    {
      key: 'tenure',
      label: 'مدّة الخدمة',
      group: 'الخدمة',
      icon: 'clock',
      color: '#1D46A6',
      /* السنوات أوّلًا: من أمضى ثلاث سنوات لا يُقال عنه «٣٧ شهرًا». */
      value: years > 0 ? `${years} سنة` : `${months} شهرًا`,
      numeric: true,
      note: date(e.hireDate),
      hint: 'كم مضى منذ تعيينه — وعليه تُبنى استحقاقات الأقدمية.',
    },
    {
      key: 'endDate',
      label: 'انتهاء الخدمة',
      group: 'الخدمة',
      icon: 'logout',
      color: e.endDate ? '#DC2626' : '#9BA3AE',
      value: e.endDate ? date(e.endDate) : 'على رأس العمل',
      numeric: !!e.endDate,
      hint: 'يوم خروجه — إن خرج.',
    },
    {
      key: 'code',
      label: 'الكود',
      group: 'الخدمة',
      icon: 'clipboard',
      color: '#8791A1',
      value: e.code,
      numeric: true,
      hint: 'رقمه الوظيفي في النظام.',
    },

    {
      key: 'baseSalary',
      label: 'الأساسي',
      group: 'المالية',
      icon: 'wallet',
      color: '#51A521',
      value: money(e.baseSalary),
      numeric: true,
      hint: 'الراتب الأساسي قبل البدلات والخصومات.',
    },
    {
      key: 'payslips',
      label: 'قسائم الرواتب',
      group: 'المالية',
      icon: 'receipt',
      color: '#1D46A6',
      value: (e.payslips ?? []).length,
      numeric: true,
      hint: 'عدد الشهور التي صُرف فيها راتبه.',
    },
    {
      key: 'adjustments',
      label: 'التسويات',
      group: 'المالية',
      icon: 'arrowRightLeft',
      color: '#BF930B',
      value: (e.adjustments ?? []).length,
      numeric: true,
      note:
        (e.adjustments ?? []).length === 0 ? 'لا بدلات ولا خصومات' : undefined,
      hint: 'البدلات والخصومات المسجَّلة عليه.',
    },

    {
      key: 'leaveBalance',
      label: 'رصيد الإجازة',
      group: 'الإجازات',
      icon: 'home',
      color: Number(e.annualLeaveBalance) > 0 ? '#5596FF' : '#DC2626',
      value: `${Number(e.annualLeaveBalance)} يوم`,
      numeric: true,
      note: Number(e.annualLeaveBalance) === 0 ? 'نفد رصيده' : undefined,
      hint: 'ما تبقّى له من الإجازة السنوية.',
    },
    {
      key: 'leaves',
      label: 'طلبات الإجازة',
      group: 'الإجازات',
      icon: 'calendar',
      color: '#6F7784',
      value: (e.leaves ?? []).length,
      numeric: true,
      hint: 'كل ما تقدّم به من طلبات.',
    },

    {
      key: 'jobTitle',
      label: 'المسمّى الوظيفي',
      group: 'التنظيم',
      icon: 'user',
      color: '#3672EA',
      value: e.jobTitle || '—',
      hint: 'دوره كما هو مسجَّل.',
    },
    {
      key: 'department',
      label: 'القسم',
      group: 'التنظيم',
      icon: 'building',
      color: '#1D46A6',
      value: e.department || '—',
      hint: 'الإدارة التي يعمل بها.',
    },
    {
      key: 'manager',
      label: 'المدير المباشر',
      group: 'التنظيم',
      icon: 'users',
      color: '#5596FF',
      value: e.manager?.name ?? '—',
      note: e.manager ? undefined : 'لم يُسنَد',
      hint: 'من يرفع إليه تقاريره.',
    },
    {
      key: 'reports',
      label: 'يتبعه',
      group: 'التنظيم',
      icon: 'users',
      color: '#51A521',
      value: (e.reports ?? []).length,
      numeric: true,
      note: (e.reports ?? []).length === 0 ? 'لا مرؤوسين' : 'موظّفًا',
      hint: 'من يرفعون إليه — وهو ما يميّز المدير من الموظّف.',
    },
    {
      key: 'login',
      label: 'حساب الدخول',
      group: 'التنظيم',
      icon: 'settings',
      color: e.account ? '#51A521' : '#9BA3AE',
      value: e.account
        ? (MEMBERSHIP_ROLE_MAP[e.account.role as MembershipRole]?.label ??
          e.account.role)
        : 'لا يوجد',
      note: e.account
        ? e.account.lastLoginAt
          ? `آخر دخول ${dateTime(e.account.lastLoginAt)}`
          : 'لم يدخل بعد'
        : 'لا يستطيع فتح النظام',
      hint: 'صلاحيته داخل النظام — إن كان له حساب.',
    },
  ];
}

export function EmployeeProfile() {
  const { id = '' } = useParams();
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [terminating, setTerminating] = useState(false);

  const canEdit = atLeast('ADMIN');

  const profile = useQuery({
    queryKey: ['employees', id],
    queryFn: () => api<Profile>(`/hr/employees/${id}`),
  });

  if (profile.isError) {
    return (
      <ErrorState error={profile.error} onRetry={() => profile.refetch()} />
    );
  }

  const e = profile.data;

  return (
    <div className="page">
      <PageHead
        title={e?.name ?? 'ملف موظف'}
        subtitle={
          [e?.jobTitle, e?.department].filter(Boolean).join(' — ') || undefined
        }
        actions={
          e && (
            <>
              <Link to="/hr/employees" className="btn">
                رجوع
              </Link>
              {canEdit && (
                <button className="btn" onClick={() => setEditing(true)}>
                  تعديل
                </button>
              )}
              {canEdit && e.status !== 'TERMINATED' && (
                <button
                  className="btn btn-danger"
                  onClick={() => setTerminating(true)}
                >
                  إنهاء الخدمة
                </button>
              )}
            </>
          )
        }
      />

      {e && (
        <>
          <RecordStats
            items={employeeCards(e)}
            storageKey="goyone.stats.employee"
            /* الافتراضي: ما كان معروضًا قبل أن يصير الاختيار متاحًا. */
            defaults={['status', 'hireDate', 'baseSalary', 'leaveBalance']}
          />

          <div className="builder">
            <div className="card">
              <div className="card-head">
                <h2 className="card-title">البيانات</h2>
              </div>
              <div className="card-body">
                <div className="form-grid">
                  <Field label="الكود" value={e.code} num />
                  <Field label="المسمّى الوظيفي" value={e.jobTitle} />
                  <Field label="القسم" value={e.department} />
                  <Field
                    label="المدير المباشر"
                    value={e.manager?.name ?? null}
                  />
                  <Field label="البريد" value={e.email} num />
                  <Field label="الهاتف" value={e.phone} num />
                  <Field label="الرقم القومي" value={e.nationalId} num />
                  <Field
                    label="انتهاء الخدمة"
                    value={e.endDate ? date(e.endDate) : null}
                    num
                  />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2 className="card-title">حساب الدخول</h2>
              </div>
              <div className="card-body">
                {e.account ? (
                  <div className="form-grid">
                    <Field label="البريد" value={e.account.email} num />
                    <Field
                      label="الدور"
                      value={
                        MEMBERSHIP_ROLE_MAP[e.account.role]?.label ??
                        e.account.role
                      }
                    />
                    <Field
                      label="آخر دخول"
                      value={
                        e.account.lastLoginAt
                          ? dateTime(e.account.lastLoginAt)
                          : 'لم يدخل بعد'
                      }
                      num
                    />
                  </div>
                ) : (
                  /*
                   * الموظف بلا حساب حالة مشروعة لا نقص.
                   *
                   * عامل المخزن أو السائق له ملف ومرتب وحضور ولا يحتاج دخولًا
                   * إلى النظام — وفرض حساب على كل موظف يستهلك مقاعد الباقة بلا
                   * فائدة.
                   */
                  <>
                    <p className="mute">لا حساب دخول لهذا الموظف.</p>
                    <p className="tiny mute mt-2">
                      وهذا طبيعي لمن لا يحتاج استعمال النظام. لإعطائه حسابًا،
                      ادعُه من{' '}
                      <Link to="/settings/users" className="link">
                        المستخدمين
                      </Link>
                      .
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {e.reports.length > 0 && (
            <div className="card mt-4">
              <div className="card-head">
                <h2 className="card-title">يتبعونه</h2>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <tbody>
                    {e.reports.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <Link
                            to={`/hr/employees/${r.id}`}
                            className="cell-strong link"
                          >
                            {r.name}
                          </Link>
                          <div className="tiny mute">{r.jobTitle}</div>
                        </td>
                        <td>
                          <Badge kind={EMPLOYEE_STATUS_MAP[r.status].kind}>
                            {EMPLOYEE_STATUS_MAP[r.status].label}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <EmployeeDocumentsCard employeeId={e.id} />

          <EmployeeBenefitsCard employeeId={e.id} />

          <PerformanceCard employeeId={e.id} />

          <LearningCard employeeId={e.id} />

          <RelationsCard employeeId={e.id} />

          <div className="card mt-4">
            <div className="card-head">
              <h2 className="card-title">الإجازات</h2>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>النوع</th>
                    <th>من</th>
                    <th>إلى</th>
                    <th>الأيام</th>
                    <th>الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {e.leaves.map((l) => (
                    <tr key={l.id}>
                      <td>{LEAVE_TYPE_MAP[l.type].label}</td>
                      <td className="num cell-mute">{date(l.fromDate)}</td>
                      <td className="num cell-mute">{date(l.toDate)}</td>
                      <td className="num">{Number(l.days)}</td>
                      <td>
                        <Badge kind={LEAVE_STATUS_MAP[l.status].kind}>
                          {LEAVE_STATUS_MAP[l.status].label}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {e.leaves.length === 0 && (
                    <tr>
                      <td colSpan={5} className="cell-mute">
                        لا إجازات مسجَّلة.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="builder mt-4">
            <div className="card">
              <div className="card-head">
                <h2 className="card-title">بنود المرتب</h2>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <tbody>
                    {e.adjustments.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <span className="cell-strong">
                            {ADJUSTMENT_KIND_MAP[a.kind].label}
                          </span>
                          {a.reason && (
                            <div className="tiny mute">{a.reason}</div>
                          )}
                        </td>
                        <td className="num cell-mute">
                          {periodLabel(a.period)}
                        </td>
                        <td
                          className={`num ${ADJUSTMENT_KIND_MAP[a.kind].sign === 1 ? 'pos' : 'neg'}`}
                        >
                          {money(a.amount)}
                        </td>
                      </tr>
                    ))}
                    {e.adjustments.length === 0 && (
                      <tr>
                        <td className="cell-mute">لا بنود.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2 className="card-title">القسائم</h2>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <tbody>
                    {e.payslips.map((p) => (
                      <tr key={p.id}>
                        <td className="cell-strong">
                          {periodLabel(p.run.period)}
                        </td>
                        <td className="num">{money(p.netPay)}</td>
                      </tr>
                    ))}
                    {e.payslips.length === 0 && (
                      <tr>
                        <td className="cell-mute">لا قسائم بعد.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {editing && e && (
        <EmployeeDialog employee={e} onClose={() => setEditing(false)} />
      )}

      {terminating && e && (
        <TerminateDialog
          employee={e}
          onClose={() => setTerminating(false)}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['employees'] });
            setTerminating(false);
          }}
        />
      )}
      <div className="mt-4">
        <Attachments entity="employee" entityId={id} title="ملفات الموظف" />
      </div>
    </div>
  );
}

/**
 * ما رُفع عنه وما صدر في حقّه.
 *
 * وإنذارٌ سقط أثرُه يُعرض باهتًا لا يُخفى: من يقرأ الملفَّ يحتاج أن يرى
 * أنّه كان — وأنّه سقط. وإخفاؤه يجعل السجلَّ يكذب في الاتّجاه الآخر.
 */
function RelationsCard({ employeeId }: { employeeId: string }) {
  const { atLeast } = useAuth();
  const canRead = atLeast('ADMIN');

  const rel = useQuery({
    queryKey: ['relations', 'employee', employeeId],
    queryFn: () =>
      api<{
        cases: {
          id: string;
          number: string;
          subject: string;
          kindLabel: string;
          kindKind: string;
          statusLabel: string;
          statusKind: string;
          openedOn: string | null;
        }[];
        actions: {
          id: string;
          kindLabel: string;
          kindKind: string;
          reason: string;
          issuedOn: string | null;
          expiresOn: string | null;
          lapsed: boolean;
          acknowledgedAt: string | null;
        }[];
      }>(`/hr/relations/employees/${employeeId}`),
    enabled: canRead,
  });

  if (!canRead) return null;

  const d = rel.data;
  const empty =
    d !== undefined && d.cases.length === 0 && d.actions.length === 0;

  return (
    <div className="card mt-4">
      <div className="card-head">
        <h2 className="card-title">الحالات والإجراءات</h2>
        <Link className="link tiny" to="/hr/relations">
          علاقات الموظفين
        </Link>
      </div>

      {rel.isPending && <div className="skeleton" style={{ height: 80 }} />}

      {empty && (
        <p className="tiny mute">لا حالاتٍ رُفعت عنه ولا إجراءاتٍ صدرت.</p>
      )}

      {d && d.cases.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>الحالة</th>
                <th>النوع</th>
                <th>فُتحت</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {d.cases.map((x) => (
                <tr key={x.id}>
                  <td>
                    <Link className="link" to={`/hr/relations/cases/${x.id}`}>
                      {x.number}
                    </Link>
                    <div className="tiny mute">{x.subject}</div>
                  </td>
                  <td>
                    <Badge kind={x.kindKind}>{x.kindLabel}</Badge>
                  </td>
                  <td className="num cell-mute">{date(x.openedOn ?? '')}</td>
                  <td>
                    <Badge kind={x.statusKind}>{x.statusLabel}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {d && d.actions.length > 0 && (
        <div className="table-wrap mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الإجراء</th>
                <th>السبب</th>
                <th>صدر</th>
                <th>يسقط</th>
                <th>أُبلغ</th>
              </tr>
            </thead>
            <tbody>
              {d.actions.map((a) => (
                <tr key={a.id} style={a.lapsed ? { opacity: 0.55 } : undefined}>
                  <td>
                    <Badge kind={a.kindKind}>{a.kindLabel}</Badge>
                  </td>
                  <td className="cell-mute">{a.reason}</td>
                  <td className="num cell-mute">{date(a.issuedOn ?? '')}</td>
                  <td className="num cell-mute">
                    {a.expiresOn ? date(a.expiresOn) : '—'}
                    {a.lapsed && <div className="tiny">سقط أثرُه</div>}
                  </td>
                  <td className="num cell-mute">
                    {a.acknowledgedAt ? date(a.acknowledgedAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * تدريبُه كما هو في ملفّه.
 *
 * ما ينقصه وما التحق به معًا: احتياجٌ مكتوبٌ منذ سنةٍ بلا دورةٍ يقول شيئًا
 * عن الشركة لا عن صاحبه، وهو ما لا يظهر في تقرير الدورات.
 */
function LearningCard({ employeeId }: { employeeId: string }) {
  const { atLeast } = useAuth();
  const canRead = atLeast('MANAGER');

  const learning = useQuery({
    queryKey: ['learning', 'employee', employeeId],
    queryFn: () =>
      api<{
        needs: {
          id: string;
          title: string;
          statusLabel: string;
          statusKind: string;
          sourceLabel: string;
          priorityLabel: string;
          overdue: boolean;
        }[];
        enrollments: {
          id: string;
          statusLabel: string;
          statusKind: string;
          score: number | null;
          certificateNo: string | null;
          session: {
            id: string;
            startsOn: string | null;
            course: { id: string; title: string };
          };
        }[];
      }>(`/hr/learning/employees/${employeeId}`),
    enabled: canRead,
  });

  if (!canRead) return null;

  const d = learning.data;
  const empty =
    d !== undefined && d.needs.length === 0 && d.enrollments.length === 0;

  return (
    <div className="card mt-4">
      <div className="card-head">
        <h2 className="card-title">التدريب</h2>
        <Link className="link tiny" to="/hr/learning">
          التدريب والتطوير
        </Link>
      </div>

      {learning.isPending && (
        <div className="skeleton" style={{ height: 80 }} />
      )}

      {empty && (
        <p className="tiny mute">لا احتياجاتٍ مكتوبةً ولا دوراتٍ التحق بها.</p>
      )}

      {d && d.needs.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>ما ينقصه</th>
                <th>من أين</th>
                <th>الأولويّة</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {d.needs.map((n) => (
                <tr key={n.id}>
                  <td>
                    {n.title}
                    {n.overdue && (
                      <span
                        className="tiny"
                        style={{ color: 'var(--warning)' }}
                      >
                        {' '}
                        · مضى موعدُه
                      </span>
                    )}
                  </td>
                  <td className="cell-mute">{n.sourceLabel}</td>
                  <td className="cell-mute">{n.priorityLabel}</td>
                  <td>
                    <Badge kind={n.statusKind}>{n.statusLabel}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {d && d.enrollments.length > 0 && (
        <div className="table-wrap mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الدورة</th>
                <th>متى</th>
                <th>الدرجة</th>
                <th>الشهادة</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {d.enrollments.map((x) => (
                <tr key={x.id}>
                  <td>
                    <Link
                      className="link"
                      to={`/hr/learning/sessions/${x.session.id}`}
                    >
                      {x.session.course.title}
                    </Link>
                  </td>
                  <td className="num cell-mute">
                    {date(x.session.startsOn ?? '')}
                  </td>
                  <td className="num">
                    {x.score === null ? '—' : num(x.score)}
                  </td>
                  <td className="cell-mute">{x.certificateNo ?? '—'}</td>
                  <td>
                    <Badge kind={x.statusKind}>{x.statusLabel}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * أداؤه كما هو في ملفّه.
 *
 * المُرسَلُ وحدَه: المسوّدةُ رأيُ كاتبها قبل أن يتمّه، وعرضُها في ملفّ
 * صاحبها يجعل نصفَ حكمٍ يُقرأ حكمًا. والبابُ الكامل — الدرجاتُ والخلاصةُ
 * والتوصية — في دورته، ومن هنا رابطٌ إليه.
 */
function PerformanceCard({ employeeId }: { employeeId: string }) {
  const { atLeast } = useAuth();
  const canRead = atLeast('ADMIN');

  const perf = useQuery({
    queryKey: ['performance', 'employee', employeeId],
    queryFn: () =>
      api<{
        reviews: {
          id: string;
          overall: number | null;
          scaleMax: number;
          bandLabel: string | null;
          bandKind: string | null;
          recommendationLabel: string | null;
          statusLabel: string;
          statusKind: string;
          cycle: { id: string; name: string };
        }[];
        goals: {
          id: string;
          title: string;
          progress: number;
          statusLabel: string;
          statusKind: string;
          overdue: boolean;
        }[];
        plans: {
          id: string;
          reason: string;
          startsOn: string | null;
          endsOn: string | null;
          statusLabel: string;
          statusKind: string;
        }[];
      }>(`/hr/performance/employees/${employeeId}`),
    enabled: canRead,
  });

  if (!canRead) return null;

  const d = perf.data;
  const empty =
    d !== undefined &&
    d.reviews.length === 0 &&
    d.goals.length === 0 &&
    d.plans.length === 0;

  return (
    <div className="card mt-4">
      <div className="card-head">
        <h2 className="card-title">الأداء</h2>
        <Link className="link tiny" to="/hr/performance">
          إدارة الأداء
        </Link>
      </div>

      {perf.isPending && <div className="skeleton" style={{ height: 80 }} />}

      {empty && (
        <p className="tiny mute">
          لا تقييماتٍ مُرسَلةً ولا أهدافًا مكتوبة. والأهدافُ تُكتب أوّلَ المدّة
          لتُقاس في آخرها — لا بعد أن تنتهي.
        </p>
      )}

      {d && d.reviews.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>الدورة</th>
                <th>المحصّلة</th>
                <th>المرتبة</th>
                <th>التوصية</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {d.reviews.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link
                      className="link"
                      to={`/hr/performance/cycles/${r.cycle.id}`}
                    >
                      {r.cycle.name}
                    </Link>
                  </td>
                  <td className="num">
                    {r.overall === null
                      ? '—'
                      : `${num(r.overall)} / ${num(r.scaleMax)}`}
                  </td>
                  <td>
                    {r.bandLabel ? (
                      <Badge kind={r.bandKind ?? 'neutral'}>
                        {r.bandLabel}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="cell-mute">{r.recommendationLabel ?? '—'}</td>
                  <td>
                    <Badge kind={r.statusKind}>{r.statusLabel}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {d && d.goals.length > 0 && (
        <div className="table-wrap mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الهدف</th>
                <th>ما بلغه</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {d.goals.map((g) => (
                <tr key={g.id}>
                  <td>
                    {g.title}
                    {g.overdue && (
                      <span
                        className="tiny"
                        style={{ color: 'var(--warning)' }}
                      >
                        {' '}
                        · مضى موعدُه
                      </span>
                    )}
                  </td>
                  <td className="num">{num(g.progress)}٪</td>
                  <td>
                    <Badge kind={g.statusKind}>{g.statusLabel}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {d && d.plans.length > 0 && (
        <div className="table-wrap mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>خطّة تحسين</th>
                <th>المدّة</th>
                <th>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {d.plans.map((x) => (
                <tr key={x.id}>
                  <td>{x.reason}</td>
                  <td className="num cell-mute">
                    {date(x.startsOn ?? '')} — {date(x.endsOn ?? '')}
                  </td>
                  <td>
                    <Badge kind={x.statusKind}>{x.statusLabel}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  num,
}: {
  label: string;
  value: string | null;
  num?: boolean;
}) {
  return (
    <div className="field">
      <span className="label">{label}</span>
      <span className={num ? 'num' : undefined} dir={num ? 'ltr' : undefined}>
        {value || '—'}
      </span>
    </div>
  );
}

/**
 * إنهاء الخدمة لا حذف.
 *
 * للموظف حضور ومرتبات وقسائم صُرفت — سجلّ مالي لا يُمحى بانتهاء عمله،
 * وحذفه كان سيسقط معه قسائم في دورات أُقفلت من شهور.
 */
function TerminateDialog({
  employee,
  onClose,
  onDone,
}: {
  employee: Profile;
  onClose: () => void;
  onDone: () => void;
}) {
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));

  const terminate = useMutation({
    mutationFn: () =>
      api(`/hr/employees/${employee.id}/terminate`, {
        method: 'POST',
        body: { endDate },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title="إنهاء الخدمة"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            onClick={() => terminate.mutate()}
            disabled={terminate.isPending}
          >
            {terminate.isPending ? 'جارٍ' : 'إنهاء الخدمة'}
          </button>
        </>
      }
    >
      {terminate.isError && (
        <div className="alert alert-danger mb-4">
          {terminate.error instanceof Error
            ? terminate.error.message
            : 'تعذّرت العملية.'}
        </div>
      )}

      <p>
        سيخرج <span className="strong">{employee.name}</span> من كشوف الحضور ومن
        دورات المرتب التالية.
      </p>

      <div className="field mt-4">
        <label className="label req" htmlFor="t-end">
          تاريخ انتهاء الخدمة
        </label>
        <input
          id="t-end"
          className="input num"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>

      <div className="alert alert-info mt-4">
        ملفه وحضوره وقسائمه السابقة تبقى كما هي — السجل المالي لا يُمحى.
      </div>

      {employee.account && (
        <div className="alert alert-warning mt-3">
          له حساب دخول على النظام، وإنهاء الخدمة لا يوقفه. أوقفه من{' '}
          <Link to="/settings/users" className="link">
            المستخدمين
          </Link>{' '}
          إن أردت منع دخوله.
        </div>
      )}
    </Modal>
  );
}
